/**
 * refreshJobsStore — the background refresh registry (the modal is gone).
 *
 * Starting a refresh `track()`s the job here; RefreshTracker (mounted once
 * in the app shell) opens the SSE subscription for every tracked job and
 * folds its frames through `jobProgressReducer`, so progress survives any
 * navigation — the global RefreshChip renders it from EVERY page. Reloads
 * and already-running jobs are adopted from `active_job` on any repo
 * status/summary the app happens to fetch (`adopt`) — `subscribeJob`
 * replays persisted events, so a late fold is still correct.
 *
 * A SUCCEEDED job lingers briefly (the chip's "Map updated" beat), then
 * `dismiss()` removes it (RefreshTracker owns that timer). Failed/cancelled
 * jobs are only ever dismissed explicitly, from the chip's popover.
 */

import { create } from 'zustand';

import type { JobFrame, Ulid } from '../../api/types';
import {
  initialJobProgress,
  jobProgressReducer,
  type JobProgressState,
} from './jobProgress';

export interface TrackedRefreshJob {
  jobId: Ulid;
  repoId: Ulid;
  state: JobProgressState;
  /** ms timestamp of the job.done fold — RefreshTracker schedules dismissal. */
  doneAt: number | null;
}

export interface RefreshJobsState {
  /** jobId → tracked job, insertion-ordered via `order`. */
  jobs: Record<string, TrackedRefreshJob>;
  order: Ulid[];
  /** True while the chip's popover is open — auto-dismissal waits. */
  inspecting: boolean;

  /** Register a just-started job (CostPreviewDialog confirm). Idempotent. */
  track: (repoId: Ulid, jobId: Ulid) => void;
  setInspecting: (inspecting: boolean) => void;
  /**
   * Adopt a server-reported active job (page reload / another tab started
   * it): same as track, but only for running/queued jobs. Idempotent.
   */
  adopt: (repoId: Ulid, jobId: Ulid) => void;
  applyFrame: (jobId: Ulid, frame: JobFrame) => void;
  connectionError: (jobId: Ulid) => void;
  dismiss: (jobId: Ulid) => void;
  reset: () => void;
}

export const useRefreshJobs = create<RefreshJobsState>((set, get) => {
  const add = (repoId: Ulid, jobId: Ulid) => {
    if (get().jobs[jobId] !== undefined) return;
    set((s) => ({
      jobs: {
        ...s.jobs,
        [jobId]: { jobId, repoId, state: initialJobProgress, doneAt: null },
      },
      order: [...s.order, jobId],
    }));
  };

  return {
    jobs: {},
    order: [],
    inspecting: false,

    track(repoId, jobId) {
      add(repoId, jobId);
    },

    setInspecting(inspecting) {
      set({ inspecting });
    },

    adopt(repoId, jobId) {
      add(repoId, jobId);
    },

    applyFrame(jobId, frame) {
      const job = get().jobs[jobId];
      if (job === undefined) return;
      const state = jobProgressReducer(job.state, { kind: 'frame', frame });
      set((s) => ({
        jobs: {
          ...s.jobs,
          [jobId]: {
            ...job,
            state,
            doneAt:
              job.doneAt ?? (state.done !== null ? Date.now() : null),
          },
        },
      }));
    },

    connectionError(jobId) {
      const job = get().jobs[jobId];
      if (job === undefined) return;
      set((s) => ({
        jobs: {
          ...s.jobs,
          [jobId]: { ...job, state: jobProgressReducer(job.state, { kind: 'connection-error' }) },
        },
      }));
    },

    dismiss(jobId) {
      set((s) => {
        if (s.jobs[jobId] === undefined) return s;
        const jobs = { ...s.jobs };
        delete jobs[jobId];
        return { jobs, order: s.order.filter((id) => id !== jobId) };
      });
    },

    reset() {
      set({ jobs: {}, order: [], inspecting: false });
    },
  };
});

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/** Tracked jobs, oldest first (the chip summarizes the first). */
export function selectTrackedJobs(s: RefreshJobsState): TrackedRefreshJob[] {
  return s.order
    .map((id) => s.jobs[id])
    .filter((j): j is TrackedRefreshJob => j !== undefined);
}

/** The repo's ACTIVE tracked job (running/queued), else null. */
export function selectRepoActiveJob(
  s: RefreshJobsState,
  repoId: Ulid,
): TrackedRefreshJob | null {
  for (const job of Object.values(s.jobs)) {
    if (job.repoId === repoId && job.state.done === null) return job;
  }
  return null;
}

export function selectRepoRefreshing(s: RefreshJobsState, repoId: Ulid): boolean {
  return s.order.some((id) => {
    const job = s.jobs[id];
    return job !== undefined && job.repoId === repoId && job.state.done === null;
  });
}
